import React, { useState } from 'react';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import TypeaheadSelect from '../../components/TypeaheadSelect';
import ContactForm from '../../components/ContactForm';
import {
  Trash2,
  Plus,
} from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';

/* ───────────── Edit Account Modal ───────────── */

export function EditAccountModal({
  showEditModal,
  setShowEditModal,
  formData,
  setFormData,
  handleUpdate,
  stages,
  leadSources,
  users,
  ownershipRoleTypes,
  repFirms,
  addOwner,
  updateOwner,
  removeOwner,
}) {
  return (
    <Modal
      isOpen={showEditModal}
      onClose={() => setShowEditModal(false)}
      title="Edit Account"
      size="lg"
    >
      <form onSubmit={handleUpdate}>
        <div className="modal-body">
          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Account Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Industry</label>
              <input
                type="text"
                className="form-input"
                value={formData.industry || ''}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Website</label>
              <input
                type="url"
                className="form-input"
                value={formData.website || ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">LinkedIn Company Page</label>
              <input
                type="url"
                className="form-input"
                placeholder="https://linkedin.com/company/..."
                value={formData.linkedin_company_url || ''}
                onChange={(e) => setFormData({ ...formData, linkedin_company_url: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Stage</label>
              <select
                className="form-select"
                value={formData.stage_id || ''}
                onChange={(e) => setFormData({ ...formData, stage_id: e.target.value })}
              >
                <option value="">Select Stage</option>
                {stages.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Lead Source</label>
              <select
                className="form-select"
                value={formData.lead_source_id || ''}
                onChange={(e) => setFormData({ ...formData, lead_source_id: e.target.value })}
              >
                <option value="">Select Lead Source</option>
                {leadSources.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Rep Firm</label>
              <select
                className="form-select"
                value={formData.rep_firm_id || ''}
                onChange={(e) => setFormData({ ...formData, rep_firm_id: e.target.value })}
              >
                <option value="">Select Rep Firm</option>
                {repFirms.map(rf => (
                  <option key={rf.id} value={rf.id}>{rf.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Owners</label>
            {formData.owners?.map((owner, index) => (
              <div key={index} className="owner-row">
                <select
                  className="form-select"
                  value={owner.user_id || ''}
                  onChange={(e) => updateOwner(index, 'user_id', e.target.value)}
                >
                  <option value="">Select User</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <select
                  className="form-select"
                  value={owner.ownership_role_type_id || ''}
                  onChange={(e) => updateOwner(index, 'ownership_role_type_id', e.target.value)}
                >
                  <option value="">Role Type</option>
                  {ownershipRoleTypes.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={owner.is_primary || false}
                    onChange={(e) => updateOwner(index, 'is_primary', e.target.checked)}
                  />
                  Primary
                </label>
                <button type="button" className="btn btn-icon" onClick={() => removeOwner(index)}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addOwner}>
              <Plus size={16} />
              Add Owner
            </button>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Save Changes
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ───────────── Delete Account Modal ───────────── */

export function DeleteAccountModal({
  showDeleteConfirm,
  setShowDeleteConfirm,
  account,
  deleteContacts,
  setDeleteContacts,
  handleDelete,
}) {
  return (
    <Modal
      isOpen={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      title="Delete Account"
      size="sm"
    >
      <div className="modal-body">
        <p>Are you sure you want to delete <strong>{account.name}</strong>?</p>
        {account.contacts?.length > 0 && (
          <div className="mt-4">
            <label className="checkbox-wrapper">
              <input
                type="checkbox"
                className="checkbox"
                checked={deleteContacts}
                onChange={(e) => setDeleteContacts(e.target.checked)}
              />
              Also delete {account.contacts.length} contact(s)
            </label>
          </div>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
          Cancel
        </button>
        <button className="btn btn-danger" onClick={handleDelete}>
          Delete Account
        </button>
      </div>
    </Modal>
  );
}

/* ───────────── Log Activity Modal ───────────── */

export function LogActivityModal({
  showActivityModal,
  setShowActivityModal,
  activityForm,
  setActivityForm,
  handleLogActivity,
  contacts,
  error,
}) {
  return (
    <Modal
      isOpen={showActivityModal}
      onClose={() => setShowActivityModal(false)}
      title="Log Activity"
    >
      <form onSubmit={handleLogActivity}>
        <div className="modal-body">
          {error && <div className="alert alert-error" role="alert">{error}</div>}
          <div className="form-group">
            <label className="form-label">Type *</label>
            <select
              className="form-select"
              value={activityForm.type}
              onChange={(e) => setActivityForm({ ...activityForm, type: e.target.value })}
              required
            >
              <option value="Call">Call</option>
              <option value="Email">Email</option>
              <option value="Meeting">Meeting</option>
              <option value="Note">Note</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Contact</label>
            <select
              className="form-select"
              value={activityForm.contact_id}
              onChange={(e) => setActivityForm({ ...activityForm, contact_id: e.target.value })}
            >
              <option value="">Select Contact</option>
              {contacts?.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={activityForm.description}
              onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
              placeholder="Add notes about this activity..."
            />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setShowActivityModal(false)}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Log Activity
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ───────────── Create Action Item Modal ───────────── */

export function CreateTaskModal({
  showTaskModal,
  setShowTaskModal,
  taskForm,
  setTaskForm,
  handleCreateTask,
  users,
  contacts,
  error,
}) {
  return (
    <Modal
      isOpen={showTaskModal}
      onClose={() => setShowTaskModal(false)}
      title="Add Action Item"
    >
      <form onSubmit={handleCreateTask}>
        <div className="modal-body">
          {error && <div className="alert alert-error" role="alert">{error}</div>}
          <div className="form-group">
            <label className="form-label">Action Item *</label>
            <input
              type="text"
              className="form-input"
              value={taskForm.title}
              onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Due Date</label>
            <input
              type="datetime-local"
              className="form-input"
              value={taskForm.due_date}
              onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Assign To</label>
            <select
              className="form-select"
              value={taskForm.assigned_to}
              onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
            >
              <option value="">Assign to me</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Related Contact</label>
            <select
              className="form-select"
              value={taskForm.contact_id}
              onChange={(e) => setTaskForm({ ...taskForm, contact_id: e.target.value })}
            >
              <option value="">Select Contact</option>
              {contacts?.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setShowTaskModal(false)}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Create Action Item
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ───────────── Address Modal ───────────── */

export function AddressModal({
  showAddressModal,
  setShowAddressModal,
  editingAddress,
  addressForm,
  setAddressForm,
  handleSaveAddress,
  error,
}) {
  return (
    <Modal
      isOpen={showAddressModal}
      onClose={() => setShowAddressModal(false)}
      title={editingAddress ? 'Edit Address' : 'Add Address'}
    >
      <form onSubmit={handleSaveAddress}>
        <div className="modal-body">
          {error && <div className="alert alert-error" role="alert">{error}</div>}
          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Address Type</label>
              <select
                className="form-select"
                value={addressForm.address_type}
                onChange={(e) => setAddressForm({ ...addressForm, address_type: e.target.value })}
              >
                <option value="Headquarters">Headquarters</option>
                <option value="Shipping">Shipping</option>
                <option value="Billing">Billing</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">&nbsp;</label>
              <div className="checkbox-group">
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={addressForm.is_headquarters}
                    onChange={(e) => setAddressForm({ ...addressForm, is_headquarters: e.target.checked })}
                  />
                  Is Headquarters
                </label>
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={addressForm.is_default_shipping}
                    onChange={(e) => setAddressForm({ ...addressForm, is_default_shipping: e.target.checked })}
                  />
                  Default Shipping
                </label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address Line 1</label>
            <input
              type="text"
              className="form-input"
              value={addressForm.address_line1}
              onChange={(e) => setAddressForm({ ...addressForm, address_line1: e.target.value })}
              placeholder="Street address"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Address Line 2</label>
            <input
              type="text"
              className="form-input"
              value={addressForm.address_line2}
              onChange={(e) => setAddressForm({ ...addressForm, address_line2: e.target.value })}
              placeholder="Suite, unit, building, etc."
            />
          </div>

          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                className="form-input"
                value={addressForm.city}
                onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">State/Province</label>
              <input
                type="text"
                className="form-input"
                value={addressForm.state}
                onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">ZIP/Postal Code</label>
              <input
                type="text"
                className="form-input"
                value={addressForm.zip_code}
                onChange={(e) => setAddressForm({ ...addressForm, zip_code: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Country</label>
              <input
                type="text"
                className="form-input"
                value={addressForm.country}
                onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setShowAddressModal(false)}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {editingAddress ? 'Save Changes' : 'Add Address'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ───────────── Contact Modal ───────────── */

export function ContactModal({
  showContactModal,
  setShowContactModal,
  editingContact,
  setEditingContact,
  contactForm,
  setContactForm,
  handleSaveContact,
  leadSources,
  repFirms,
  users,
  accounts,
  error,
}) {
  return (
    <Modal
      isOpen={showContactModal}
      onClose={() => {
        setShowContactModal(false);
        setEditingContact(null);
      }}
      title={editingContact ? 'Edit Contact' : 'New Contact'}
      size="lg"
    >
      <form onSubmit={handleSaveContact}>
        <div className="modal-body">
          {error && <div className="alert alert-error" role="alert">{error}</div>}
          <ContactForm
            contactForm={contactForm}
            setContactForm={setContactForm}
            leadSources={leadSources}
            repFirms={repFirms}
            users={users}
            accounts={accounts}
          />
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => {
            setShowContactModal(false);
            setEditingContact(null);
          }}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {editingContact ? 'Save Changes' : 'Create Contact'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ───────────── Delete Contact Confirmation ───────────── */

export function DeleteContactConfirm({
  showDeleteContactConfirm,
  setShowDeleteContactConfirm,
  contactToDelete,
  setContactToDelete,
  handleDeleteContact,
}) {
  return (
    <ConfirmDialog
      isOpen={showDeleteContactConfirm}
      onClose={() => {
        setShowDeleteContactConfirm(false);
        setContactToDelete(null);
      }}
      onConfirm={handleDeleteContact}
      title="Delete Contact"
      message={`Are you sure you want to delete "${contactToDelete?.name}"? This action cannot be undone.`}
      confirmText="Delete"
    />
  );
}

/* ───────────── Delete Address Confirmation ───────────── */

export function DeleteAddressConfirm({
  showDeleteAddressConfirm,
  setShowDeleteAddressConfirm,
  setAddressToDelete,
  confirmDeleteAddress,
}) {
  return (
    <ConfirmDialog
      isOpen={showDeleteAddressConfirm}
      onClose={() => {
        setShowDeleteAddressConfirm(false);
        setAddressToDelete(null);
      }}
      onConfirm={confirmDeleteAddress}
      title="Delete Address"
      message="Are you sure you want to delete this address? This action cannot be undone."
      confirmText="Delete"
    />
  );
}

/* ───────────── Create Opportunity Modal ───────────── */

export function CreateOpportunityModal({
  showOpportunityModal,
  setShowOpportunityModal,
  opportunityForm,
  setOpportunityForm,
  handleCreateOpportunity,
  contacts,
  users,
  opportunityStages,
  repFirms,
  products,
  addOpportunityLineItem,
  updateOpportunityLineItem,
  removeOpportunityLineItem,
  calculateOpportunityTotal,
  error,
}) {
  const [contactSearchText, setContactSearchText] = useState('');

  return (
    <Modal
      isOpen={showOpportunityModal}
      onClose={() => { setShowOpportunityModal(false); setContactSearchText(''); }}
      title="New Opportunity"
      size="lg"
    >
      <form onSubmit={handleCreateOpportunity}>
        <div className="modal-body">
          {error && <div className="alert alert-error" role="alert">{error}</div>}
          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Contact</label>
              <TypeaheadSelect
                id="acct-opp-contact"
                placeholder="Type to search contacts..."
                items={contacts || []}
                value={opportunityForm.contact_id}
                displayValue={contactSearchText}
                onChange={(id, name) => {
                  setOpportunityForm({ ...opportunityForm, contact_id: id });
                  setContactSearchText(name);
                }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Owner</label>
              <select
                className="form-select"
                value={opportunityForm.owner_id}
                onChange={(e) => setOpportunityForm({ ...opportunityForm, owner_id: e.target.value })}
              >
                <option value="">Select Owner</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Stage</label>
              <select
                className="form-select"
                value={opportunityForm.stage_id}
                onChange={(e) => setOpportunityForm({ ...opportunityForm, stage_id: e.target.value })}
              >
                <option value="">Select Stage</option>
                {opportunityStages.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Expected Close Date</label>
              <input
                type="date"
                className="form-input"
                value={opportunityForm.expected_close_date}
                onChange={(e) => setOpportunityForm({ ...opportunityForm, expected_close_date: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Rep Firm</label>
            <select
              className="form-select"
              value={opportunityForm.rep_firm_id}
              onChange={(e) => setOpportunityForm({ ...opportunityForm, rep_firm_id: e.target.value })}
            >
              <option value="">Select Rep Firm</option>
              {repFirms.map(rf => (
                <option key={rf.id} value={rf.id}>{rf.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Products</label>
            {opportunityForm.line_items.map((item, index) => (
              <div key={index} className="line-item-row">
                <select
                  className="form-select"
                  value={item.product_id}
                  onChange={(e) => updateOpportunityLineItem(index, 'product_id', e.target.value)}
                >
                  <option value="">Select Product</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.sku ? `${p.sku} - ` : ''}{p.name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  className="form-input quantity-input"
                  placeholder="Qty"
                  value={item.quantity}
                  onChange={(e) => updateOpportunityLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
                <input
                  type="number"
                  className="form-input price-input"
                  placeholder="Price"
                  value={item.unit_price}
                  onChange={(e) => updateOpportunityLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                />
                <span className="line-item-total">
                  {formatCurrency((item.quantity || 0) * (item.unit_price || 0))}
                </span>
                <button type="button" className="btn btn-icon" onClick={() => removeOpportunityLineItem(index)}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addOpportunityLineItem}>
              <Plus size={16} />
              Add Product
            </button>
            {opportunityForm.line_items.length > 0 && (
              <div className="opportunity-total">
                Total: {formatCurrency(calculateOpportunityTotal())}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              value={opportunityForm.notes}
              onChange={(e) => setOpportunityForm({ ...opportunityForm, notes: e.target.value })}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setShowOpportunityModal(false)}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Create Opportunity
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ───────────── Create Quote Modal ───────────── */

export function CreateQuoteModal({
  showQuoteModal,
  setShowQuoteModal,
  quoteForm,
  setQuoteForm,
  handleCreateQuote,
  contacts,
  addresses,
  products,
  addQuoteLineItem,
  updateQuoteLineItem,
  removeQuoteLineItem,
  calculateQuoteTotal,
  error,
}) {
  return (
    <Modal
      isOpen={showQuoteModal}
      onClose={() => setShowQuoteModal(false)}
      title="New Quote"
      size="lg"
    >
      <form onSubmit={handleCreateQuote}>
        <div className="modal-body">
          {error && <div className="alert alert-error" role="alert">{error}</div>}
          <div className="grid grid-cols-2">
            <div className="form-group">
              <label className="form-label">Contact</label>
              <select
                className="form-select"
                value={quoteForm.contact_id}
                onChange={(e) => setQuoteForm({ ...quoteForm, contact_id: e.target.value })}
              >
                <option value="">Select Contact</option>
                {contacts?.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Ship To Address</label>
              <select
                className="form-select"
                value={quoteForm.ship_to_address_id}
                onChange={(e) => setQuoteForm({ ...quoteForm, ship_to_address_id: e.target.value })}
              >
                <option value="">Select Address</option>
                {addresses?.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.address_line1}{a.city ? `, ${a.city}` : ''}{a.state ? `, ${a.state}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Estimated Ship Date</label>
            <input
              type="date"
              className="form-input"
              value={quoteForm.estimated_ship_date}
              onChange={(e) => setQuoteForm({ ...quoteForm, estimated_ship_date: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Line Items</label>
            {quoteForm.line_items.map((item, index) => (
              <div key={index} className="line-item-row">
                <select
                  className="form-select"
                  value={item.product_id}
                  onChange={(e) => updateQuoteLineItem(index, 'product_id', e.target.value)}
                >
                  <option value="">Select Product</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.sku ? `${p.sku} - ` : ''}{p.name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  className="form-input quantity-input"
                  placeholder="Qty"
                  value={item.quantity}
                  onChange={(e) => updateQuoteLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="1"
                />
                <input
                  type="number"
                  className="form-input price-input"
                  placeholder="Price"
                  value={item.unit_price}
                  onChange={(e) => updateQuoteLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                />
                <span className="line-item-total">
                  {formatCurrency((item.quantity || 0) * (item.unit_price || 0))}
                </span>
                <button type="button" className="btn btn-icon" onClick={() => removeQuoteLineItem(index)}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addQuoteLineItem}>
              <Plus size={16} />
              Add Product
            </button>
            {quoteForm.line_items.length > 0 && (
              <div className="quote-total">
                Total: {formatCurrency(calculateQuoteTotal())}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              value={quoteForm.notes}
              onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setShowQuoteModal(false)}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Create Quote
          </button>
        </div>
      </form>
    </Modal>
  );
}
